import { useAuth } from './useAuth';
import type { RolePermissions } from '../types/auth';

/**
 * Custom hook for role-based access control
 * Provides easy-to-use permission checking functions
 */
export const useRolePermissions = () => {
  const { user, userRolePermissions, hasPermission, getScope } = useAuth();

  // Check if user is super admin
  const isSuperAdmin = user?.superAdminFlag === 1;

  // Check if user has any role permissions loaded
  const hasRolePermissions = !!userRolePermissions;

  // Permission checking functions for common resources
  const canView = (resource: keyof RolePermissions) => hasPermission(resource as string, 'view');
  const canAdd = (resource: keyof RolePermissions) => hasPermission(resource as string, 'add');
  const canEdit = (resource: keyof RolePermissions) => hasPermission(resource as string, 'edit');
  const canDelete = (resource: keyof RolePermissions) => hasPermission(resource as string, 'delete');
  const canExport = (resource: keyof RolePermissions) => hasPermission(resource as string, 'export');
  const canAssign = (resource: keyof RolePermissions) => hasPermission(resource as string, 'assign');

  // Specific permission checks for common use cases
  const canManageUsers = canView('users') || canAdd('users') || canEdit('users') || canDelete('users') || canAssign('users');
  const canManageCompanies = canView('companies') || canAdd('companies') || canEdit('companies') || canDelete('companies');
  const canManageCameras = canView('cameras') || canAdd('cameras') || canEdit('cameras') || canDelete('cameras');
  const canManageEmployees = canView('employees') || canAdd('employees') || canEdit('employees') || canDelete('employees');
  const canManageDepartments = canView('departments') || canAdd('departments') || canEdit('departments') || canDelete('departments');
  const canManageCalendars = canView('calendars') || canAdd('calendars') || canEdit('calendars') || canDelete('calendars');
  const canManageEdgeDevices = canView('edge_devices') || canAdd('edge_devices') || canEdit('edge_devices') || canDelete('edge_devices');
  const canViewReports = canView('employeesAggregate') || canView('attendanceRecord') || canView('breakByEmployee') || canView('breakByDate');
  const canExportReports = canExport('employeesAggregate') || canExport('facesImages');
  const canValidateFaces = canView('validation') || canAdd('validation');
  const canViewDashboard = canView('dashboard');
  const canViewAnalytics = hasPermission('analytics', 'view') || isSuperAdmin;
  const canViewPPE = canView('ppe');
  const canViewPPESummary = hasPermission('ppe', 'summary');
  const canAccessPpeSettings = hasPermission('ppe_settings', 'view') || isSuperAdmin;
  const canEditPpeSettings = hasPermission('ppe_settings', 'edit') || isSuperAdmin;
  const canAccessPpeViolations = hasPermission('ppe_violations', 'view') || isSuperAdmin;
  const canManageVacations = canView('employeesVacation') || canAdd('employeesVacation') || canEdit('employeesVacation') || canDelete('employeesVacation');
  const canManageHolidays = canView('holidays') || canAdd('holidays') || canEdit('holidays') || canDelete('holidays');
  const canManagePolicies = canView('policy') || canAdd('policy') || canEdit('policy') || canDelete('policy');
  const canManageCompanyGroups = isSuperAdmin;
  const canManageCompanyLocations = isSuperAdmin;

  // Shift scheduling permissions
  const hasShiftAction = (action: string) => hasPermission('shifts', action);
  const canViewShifts = hasShiftAction('view');
  const canAddShifts = hasShiftAction('manage');
  const canEditShifts = hasShiftAction('manage');
  const canDeleteShifts = hasShiftAction('manage') || hasShiftAction('delete');
  const canManageShifts = canViewShifts || canAddShifts || canEditShifts || canDeleteShifts;
  const canApproveSwaps = hasShiftAction('approve');

  // Payroll permissions
  const hasPayrollAction = (action: string) => hasPermission('payroll', action);
  const canViewPayroll = hasPayrollAction('view');
  const canManagePayroll = hasPayrollAction('manage');
  const canRunPayroll = hasPayrollAction('run');
  const canApprovePayroll = hasPayrollAction('approve');
  const canExportPayroll = hasPayrollAction('export');
  const payrollScope = getScope('payroll');
  const hasCompanyWidePayrollScope = isSuperAdmin || payrollScope === 'all';

  // Custom Reports permissions
  const canViewCustomReports = hasPermission('customReports', 'view') || isSuperAdmin;
  const canCreateCustomReports = hasPermission('customReports', 'create') || isSuperAdmin;
  const canExportCustomReports = hasPermission('customReports', 'export') || isSuperAdmin;
  const canShareCustomReports = hasPermission('customReports', 'share') || isSuperAdmin;
  const canManageCustomReports = hasPermission('customReports', 'manage') || isSuperAdmin;

  // Settings page specific permission checks
  const canViewSettings = isSuperAdmin || hasRolePermissions; // Super admins can always view, others need role permissions
  
  // User management permissions for Settings page
  const canCreateUsers = canAdd('users'); // Can create users in their company
  const canEditUsers = canEdit('users'); // Can edit users in their company
  const canDeleteUsers = canDelete('users'); // Can delete users in their company
  const canViewUsers = canView('users'); // Can view users in their company
  const canViewRoles = canView('roles') || canView('users'); // Can view roles if they have roles permission OR users permission
  const canAssignUsers = canAssign('users'); // Can assign users to companies
  
  const canManageRoles = canView('roles') || canAdd('roles') || canEdit('roles') || canDelete('roles');
  const canAddRoles = canAdd('roles');
  const canEditRoles = canEdit('roles');
  const canDeleteRoles = canDelete('roles');
  const canViewRolesList = canView('roles');

  // Company management permissions for Settings page (super admin only)
  const canViewCompanies = canView('companies'); // Can view companies
  const canCreateCompanies = canAdd('companies'); // Can create companies
  const canAccessCompanyManagement = isSuperAdmin; // Only super admins can access Company Management
  const canAccessHierarchy = isSuperAdmin; // Only super admins can access Hierarchy Management
  const canAccessCompanyGroups = isSuperAdmin;
  const canAccessCompanyLocations = isSuperAdmin;

  // Snapshot permissions
  const canViewSnapshots = canView('snapshots');
  const canDeleteSnapshots = canDelete('snapshots');
  const canAccessSnapshotSettings = canView('snapshots') || canEdit('snapshots') || isSuperAdmin;
  const canEditSnapshotSettings = canEdit('snapshots') || isSuperAdmin;

  const canAnalyzeSnapshots = hasPermission('snapshots', 'analyze') || isSuperAdmin;

  // Granular permissions for company groups
  const canViewCompanyGroups = isSuperAdmin;
  const canAddCompanyGroups = isSuperAdmin;
  const canEditCompanyGroups = isSuperAdmin;
  const canDeleteCompanyGroups = isSuperAdmin;

  // Granular permissions for company locations
  const canViewCompanyLocations = isSuperAdmin;
  const canAddCompanyLocations = isSuperAdmin;
  const canEditCompanyLocations = isSuperAdmin;
  const canDeleteCompanyLocations = isSuperAdmin;

  // Settings page access based on permissions
  const canAccessUserManagement = canCreateUsers || canEditUsers || canDeleteUsers || canViewUsers || canAssignUsers;
  const canAccessSettings = canViewSettings && (canAccessUserManagement || canAccessCompanyManagement || canAccessHierarchy || canManageRoles);

  // Debug logging in development
  if (import.meta.env.DEV) {
    console.log('🔐 Role Permissions Debug:', {
      userRolePermissions,
      hasRolePermissions,
      canViewSettings,
      canCreateUsers,
      canViewUsers,
      canViewRoles,
      canAssignUsers,
      canViewCompanies,
      canCreateCompanies,
      canAccessUserManagement,
      canAccessCompanyManagement,
      canAccessHierarchy,
      canAccessSettings,
      // Raw permission checks
      usersView: hasPermission('users', 'view'),
      usersAdd: hasPermission('users', 'add'),
      usersAssign: hasPermission('users', 'assign'),
      companiesView: hasPermission('companies', 'view'),
      companiesAdd: hasPermission('companies', 'add'),
      rolesView: hasPermission('roles', 'view')
    });
  }

  return {
    // User role status
    isSuperAdmin,
    hasRolePermissions,
    
    // Basic permission checks
    canView,
    canAdd,
    canEdit,
    canDelete,
    canExport,
    canAssign,
    
    // Specific permission checks
    canManageUsers,
    canManageCompanies,
    canManageCameras,
    canManageEmployees,
    canManageDepartments,
    canManageCalendars,
    canManageEdgeDevices,
    canViewReports,
    canExportReports,
    canValidateFaces,
    canViewDashboard,
    canViewAnalytics,
    canViewPPE,
    canViewPPESummary,
    canAccessPpeSettings,
    canEditPpeSettings,
    canAccessPpeViolations,
    canManageVacations,
    canManageHolidays,
    canManagePolicies,
    canManageCompanyGroups,
    canManageCompanyLocations,
    canViewShifts,
    canAddShifts,
    canEditShifts,
    canDeleteShifts,
    canManageShifts,
    canApproveSwaps,
    canViewPayroll,
    canManagePayroll,
    canRunPayroll,
    canApprovePayroll,
    canExportPayroll,
    payrollScope,
    hasCompanyWidePayrollScope,
    
    // Settings page specific permissions
    canViewSettings,
    canCreateUsers,
    canEditUsers,
    canDeleteUsers,
    canViewUsers,
    canViewRoles,
    canAssignUsers,
    canViewCompanies,
    canCreateCompanies,
    canAccessUserManagement,
    canAccessCompanyManagement,
    canAccessHierarchy,
    canAccessCompanyGroups,
    canAccessCompanyLocations,
    canViewCompanyGroups,
    canAddCompanyGroups,
    canEditCompanyGroups,
    canDeleteCompanyGroups,
    canViewCompanyLocations,
    canAddCompanyLocations,
    canEditCompanyLocations,
    canDeleteCompanyLocations,
    canViewSnapshots,
    canDeleteSnapshots,
    canAccessSnapshotSettings,
    canEditSnapshotSettings,
    canAnalyzeSnapshots,
    canViewCustomReports,
    canCreateCustomReports,
    canExportCustomReports,
    canShareCustomReports,
    canManageCustomReports,
    canManageRoles,
    canAddRoles,
    canEditRoles,
    canDeleteRoles,
    canViewRolesList,
    canAccessSettings,
    
    // Scoping
    getScope,
    
    // Raw permissions object
    permissions: userRolePermissions,
  };
};
